import React, { useEffect, useState, useCallback, useRef } from "react";
import { Button, Container, Form, Spinner, Table, Alert } from "react-bootstrap";
import axios from "../utils/api";
import * as XLSX from "xlsx";
import toast from "react-hot-toast";

const API_URL = process.env.REACT_APP_API_URL_BASE || "http://localhost:6020/";

/**
 * Parse the Coke multi-row order sheet.
 *
 * The format repeats a 4-line block per item in column A:
 *   Row 1: Item name  (e.g. "Coca-Cola Cherry,")
 *   Row 2: Size/pack  (e.g. "20 oz bottles, 24 loose")
 *   Row 3: SKU:123456
 *   Row 4: UPC:049000018011
 *
 * Quantity (if present) is typically in a neighbouring column on one of those rows.
 */

/**
 * Figure out how many sellable bottles/cans per case from the size line.
 *
 *  "20 oz bottles, 24 loose"         → 24          (X loose)
 *  "2 ltr bottles, 8 loose"          → 8           (X loose)
 *  "7.5 oz cans, 6 pk, 4 ct"        → 6 × 4 = 24 (X pk, Y ct)
 *  "12 oz cans, 12 pk, 2 ct"        → 12 × 2 = 24
 *  "16.9 oz bottles, 6 pk"           → 6           (X pk alone)
 *  "20 oz bottles, 24 ct"            → 24          (X ct alone)
 */
function parseUnitsPerCase(sizeLine) {
  if (!sizeLine) return 1;
  const s = sizeLine.toLowerCase();

  // "X loose"
  const looseMatch = s.match(/(\d+)\s*loose/);
  if (looseMatch) return parseInt(looseMatch[1], 10);

  // "X pk, Y ct"  →  X × Y
  const pkMatch = s.match(/(\d+)\s*pk/);
  const ctMatch = s.match(/(\d+)\s*ct/);
  if (pkMatch && ctMatch) return parseInt(pkMatch[1], 10) * parseInt(ctMatch[1], 10);

  // "X pk" alone
  if (pkMatch) return parseInt(pkMatch[1], 10);

  // "X ct" alone
  if (ctMatch) return parseInt(ctMatch[1], 10);

  return 1;
}

/**
 * Parse a flat tabular CSV/Excel sheet where the first row is headers.
 * Expected columns: Name, Quantity, SKU, UPC (case-insensitive).
 * The Name column contains both the product name and size info, e.g.
 * "Coca-Cola Cherry 20 oz bottles 24 loose".
 */
function parseTabularSheet(rawRows) {
  if (rawRows.length < 2) return [];

  const headerRow = rawRows[0].map((h) => String(h).trim().toLowerCase());
  const skuCol = headerRow.findIndex((h) => h === "sku");
  const upcCol = headerRow.findIndex((h) => h === "upc");
  const nameCol = headerRow.findIndex((h) => h === "name");
  const qtyCol = headerRow.findIndex((h) => h === "quantity");

  if (skuCol === -1 || upcCol === -1) return [];

  const items = [];
  for (let i = 1; i < rawRows.length; i++) {
    const row = rawRows[i];
    const sku = String(row[skuCol] ?? "").trim();
    const upc = String(row[upcCol] ?? "").trim();
    if (!sku && !upc) continue;

    const fullName = nameCol !== -1 ? String(row[nameCol] ?? "").trim() : "";
    const qty = qtyCol !== -1 ? parseFloat(row[qtyCol]) || 0 : 0;

    // Split "Coca-Cola Cherry 20 oz bottles 24 loose" into name + size
    const sizeMatch = fullName.match(/\s+(\d+(?:\.\d+)?\s+(?:oz|ltr|ml|lt)\b.*)/i);
    const itemName = sizeMatch
      ? fullName.slice(0, fullName.length - sizeMatch[0].length).trim()
      : fullName;
    const size = sizeMatch ? sizeMatch[1].trim() : "";

    const unitsPerCase = parseUnitsPerCase(size || fullName);
    const totalUnits = qty * unitsPerCase;

    items.push({
      itemName,
      size,
      sku,
      upc,
      qty,
      unitsPerCase,
      totalUnits,
      vendor: "Coca-Cola",
    });
  }
  return items;
}

function parseCokeSheet(rawRows) {
  const items = [];
  let i = 0;

  while (i < rawRows.length) {
    const cellA = String(rawRows[i]?.[0] ?? "").trim();

    // Look for a SKU: line — then backtrack to grab name + size
    if (/^SKU:/i.test(cellA)) {
      const sku = cellA.replace(/^SKU:\s*/i, "").trim();

      // Next row should be UPC:
      const nextA = String(rawRows[i + 1]?.[0] ?? "").trim();
      const upc = /^UPC:/i.test(nextA) ? nextA.replace(/^UPC:\s*/i, "").trim() : "";

      // Previous two rows are size then name
      const sizeLine = i >= 1 ? String(rawRows[i - 1]?.[0] ?? "").trim() : "";
      const nameLine = i >= 2 ? String(rawRows[i - 2]?.[0] ?? "").trim() : "";

      // Try to find qty (cases ordered) — scan all columns on these 4 rows for a number
      let qty = 0;
      for (let r = Math.max(0, i - 2); r <= Math.min(rawRows.length - 1, i + 1); r++) {
        const row = rawRows[r] || [];
        for (let c = 1; c < row.length; c++) {
          const v = parseFloat(row[c]);
          if (!isNaN(v) && v > 0 && v === Math.floor(v) && v < 10000) {
            qty = v;
            break;
          }
        }
        if (qty) break;
      }

      const unitsPerCase = parseUnitsPerCase(sizeLine);
      const totalUnits = qty * unitsPerCase;

      items.push({
        itemName: nameLine.replace(/,\s*$/, ""),
        size: sizeLine,
        sku,
        upc,
        qty,
        unitsPerCase,
        totalUnits,
        vendor: "Coca-Cola",
      });

      i += 2; // skip past UPC line
      continue;
    }
    i++;
  }

  return items;
}

export default function SkuMap() {
  const [file, setFile] = useState(null);
  const [parsed, setParsed] = useState([]);
  const [parsing, setParsing] = useState(false);
  const [importing, setImporting] = useState(false);

  const [records, setRecords] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editRow, setEditRow] = useState({});
  const fileRef = useRef(null);

  // ── Fetch existing mappings ─────────────────────────────────────
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}api/sku-map`, { params: { search } });
      if (!data.success) throw new Error(data.message);
      setRecords(data.records);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ── Parse uploaded file ─────────────────────────────────────────
  const handleParse = async () => {
    if (!file) return;
    setParsing(true);
    setParsed([]);
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });

      let items = parseCokeSheet(raw);
      if (items.length === 0) items = parseTabularSheet(raw);
      if (items.length === 0) throw new Error("No SKU/UPC items found. Is this a Coke order sheet?");

      setParsed(items);
      toast.success(`Parsed ${items.length} items`);
    } catch (err) {
      toast.error(err.message || "Parse failed");
    } finally {
      setParsing(false);
    }
  };

  // ── Import parsed items ─────────────────────────────────────────
  const handleImport = async () => {
    if (parsed.length === 0) return;
    setImporting(true);
    try {
      const { data } = await axios.post(`${API_URL}api/sku-map/import`, {
        records: parsed,
        fileName: file?.name || "",
      });
      if (!data.success) throw new Error(data.message);
      toast.success(`Imported: ${data.upserted} new, ${data.modified} updated`);
      setParsed([]);
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      fetchData();
    } catch (err) {
      toast.error(err.message || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  // ── Inline edit ─────────────────────────────────────────────────
  const startEdit = (r) => {
    setEditId(r._id);
    setEditRow({ sku: r.sku, upc: r.upc, itemName: r.itemName, size: r.size, qty: r.qty, unitsPerCase: r.unitsPerCase, totalUnits: r.totalUnits, vendor: r.vendor });
  };

  const saveEdit = async () => {
    try {
      const { data } = await axios.put(`${API_URL}api/sku-map/${editId}`, editRow);
      if (!data.success) throw new Error(data.message);
      toast.success("Updated");
      setEditId(null);
      fetchData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this mapping?")) return;
    try {
      await axios.delete(`${API_URL}api/sku-map/${id}`);
      toast.success("Deleted");
      fetchData();
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  const handleClear = () => {
    setFile(null);
    setParsed([]);
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <Container className="mt-4 mb-5">
      <h2 style={{ fontWeight: 700, marginBottom: 4 }}>🏷️ SKU Map</h2>
      <p className="text-muted mb-4" style={{ fontSize: "0.85rem" }}>
        Upload a Coca-Cola order sheet to map SKU ↔ UPC. Search existing mappings below.
      </p>

      {/* ── Upload Section ──────────────────────────────────────── */}
      <div className="card p-4 mb-4">
        <h6 style={{ fontWeight: 600 }} className="mb-3">Upload Coke Order Sheet</h6>
        <div className="d-flex gap-2 align-items-end flex-wrap mb-3">
          <Form.Control
            ref={fileRef}
            type="file"
            accept=".xlsx,.xls,.numbers,.csv"
            onChange={(e) => { setFile(e.target.files[0]); setParsed([]); }}
            style={{ maxWidth: 340 }}
          />
          <Button
            variant="outline-primary"
            onClick={handleParse}
            disabled={!file || parsing}
          >
            {parsing ? <Spinner size="sm" animation="border" /> : "Parse"}
          </Button>
          {parsed.length > 0 && (
            <Button variant="success" onClick={handleImport} disabled={importing}>
              {importing ? <Spinner size="sm" animation="border" /> : `Import ${parsed.length} Items`}
            </Button>
          )}
          {(file || parsed.length > 0) && (
            <Button variant="outline-secondary" size="sm" onClick={handleClear}>Clear</Button>
          )}
        </div>

        {parsed.length > 0 && (
          <div className="table-responsive" style={{ maxHeight: 300, overflowY: "auto" }}>
            <Table bordered size="sm" className="mb-0" style={{ fontSize: "0.85rem" }}>
              <thead className="table-light" style={{ position: "sticky", top: 0 }}>
                <tr>
                  <th>Item Name</th>
                  <th>Size</th>
                  <th>SKU</th>
                  <th>UPC</th>
                  <th style={{ textAlign: "right" }}>Cases</th>
                  <th style={{ textAlign: "right" }}>Units/Case</th>
                  <th style={{ textAlign: "right", fontWeight: 700 }}>Total Units</th>
                </tr>
              </thead>
              <tbody>
                {parsed.map((p, i) => (
                  <tr key={i}>
                    <td>{p.itemName}</td>
                    <td>{p.size}</td>
                    <td><code>{p.sku}</code></td>
                    <td><code>{p.upc}</code></td>
                    <td style={{ textAlign: "right" }}>{p.qty || "—"}</td>
                    <td style={{ textAlign: "right" }}>{p.unitsPerCase}</td>
                    <td style={{ textAlign: "right", fontWeight: 700 }}>{p.totalUnits || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </div>

      {/* ── Search + Table ──────────────────────────────────────── */}
      <div className="d-flex gap-2 align-items-center mb-3">
        <Form.Control
          type="text"
          placeholder="Search by SKU, UPC, or item name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 360 }}
        />
        {loading && <Spinner size="sm" animation="border" />}
        <span className="text-muted ms-auto" style={{ fontSize: "0.82rem" }}>
          {records.length} mapping{records.length !== 1 ? "s" : ""}
        </span>
      </div>

      {records.length === 0 && !loading ? (
        <Alert variant="info">No SKU mappings yet. Upload a Coke order sheet above.</Alert>
      ) : (
        <div className="card p-0">
          <Table striped bordered hover responsive size="sm" className="mb-0" style={{ fontSize: "0.87rem" }}>
            <thead className="table-light">
              <tr>
                <th>Item Name</th>
                <th>Size</th>
                <th>SKU</th>
                <th>UPC</th>
                <th style={{ textAlign: "right" }}>Cases</th>
                <th style={{ textAlign: "right" }}>Units/Case</th>
                <th style={{ textAlign: "right", fontWeight: 700 }}>Total Units</th>
                <th style={{ width: 110 }}></th>
              </tr>
            </thead>
            <tbody>
              {records.map((r) =>
                editId === r._id ? (
                  <tr key={r._id}>
                    <td><Form.Control size="sm" value={editRow.itemName} onChange={(e) => setEditRow({ ...editRow, itemName: e.target.value })} /></td>
                    <td><Form.Control size="sm" value={editRow.size} onChange={(e) => setEditRow({ ...editRow, size: e.target.value })} /></td>
                    <td><Form.Control size="sm" value={editRow.sku} onChange={(e) => setEditRow({ ...editRow, sku: e.target.value })} /></td>
                    <td><Form.Control size="sm" value={editRow.upc} onChange={(e) => setEditRow({ ...editRow, upc: e.target.value })} /></td>
                    <td><Form.Control size="sm" type="number" value={editRow.qty} onChange={(e) => setEditRow({ ...editRow, qty: e.target.value })} style={{ textAlign: "right", width: 70 }} /></td>
                    <td><Form.Control size="sm" type="number" value={editRow.unitsPerCase} onChange={(e) => setEditRow({ ...editRow, unitsPerCase: e.target.value })} style={{ textAlign: "right", width: 70 }} /></td>
                    <td style={{ textAlign: "right", fontWeight: 700 }}>{(Number(editRow.qty) || 0) * (Number(editRow.unitsPerCase) || 0)}</td>
                    <td>
                      <Button size="sm" variant="success" className="me-1" onClick={saveEdit}>✓</Button>
                      <Button size="sm" variant="outline-secondary" onClick={() => setEditId(null)}>✕</Button>
                    </td>
                  </tr>
                ) : (
                  <tr key={r._id}>
                    <td>{r.itemName}</td>
                    <td>{r.size}</td>
                    <td><code>{r.sku}</code></td>
                    <td><code>{r.upc}</code></td>
                    <td style={{ textAlign: "right" }}>{r.qty || "—"}</td>
                    <td style={{ textAlign: "right" }}>{r.unitsPerCase || "—"}</td>
                    <td style={{ textAlign: "right", fontWeight: 700 }}>{r.totalUnits || "—"}</td>
                    <td>
                      <Button size="sm" variant="outline-primary" className="me-1" onClick={() => startEdit(r)}>✏️</Button>
                      <Button size="sm" variant="outline-danger" onClick={() => handleDelete(r._id)}>🗑️</Button>
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </Table>
        </div>
      )}
    </Container>
  );
}
